import { useEffect, useRef } from 'react'

import { Badge, Box, Text } from '@chakra-ui/react'

import { TranscriptSegment } from '../../hooks/useLiveTranscription'

interface LiveTranscriptProps {
  segments: TranscriptSegment[]
  interimSegment: TranscriptSegment | null
  isConnected: boolean
  error: string | null
}

const getSpeakerLabel = (speaker: number | null): string => {
  if (speaker === null) return ''
  return `Speaker ${speaker + 1}`
}

const getSpeakerColor = (speaker: number | null): string => {
  if (speaker === null) return 'gray.700'
  return speaker === 0 ? 'blue.600' : 'green.600'
}

export const LiveTranscript = ({
  segments,
  interimSegment,
  isConnected,
  error,
}: LiveTranscriptProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [segments, interimSegment])

  const hasContent = segments.length > 0 || interimSegment

  return (
    <Box className="flex flex-col h-full">
      <Box className="flex items-center gap-2 mb-3">
        <Text fontWeight="bold" fontSize="1.25rem">
          Live Transcript
        </Text>
        {isConnected ? (
          <Badge colorScheme="green" variant="solid" fontSize="0.7rem">
            LIVE
          </Badge>
        ) : error ? (
          <Badge colorScheme="red" variant="solid" fontSize="0.7rem">
            ERROR
          </Badge>
        ) : (
          <Badge colorScheme="gray" variant="solid" fontSize="0.7rem">
            CONNECTING
          </Badge>
        )}
      </Box>

      {error && (
        <Text color="red.500" fontSize="sm" mb={2}>
          {error}
        </Text>
      )}

      <Box
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        minH="200px"
        maxH="400px"
        p={3}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        {hasContent ? (
          <Box className="space-y-2">
            {segments.map((segment, index) => (
              <Box key={index}>
                {segment.speaker !== null && (
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color={getSpeakerColor(segment.speaker)}
                    fontSize="sm"
                  >
                    {getSpeakerLabel(segment.speaker)}:{' '}
                  </Text>
                )}
                <Text as="span" fontSize="md" lineHeight="1.6">
                  {segment.text}
                </Text>
              </Box>
            ))}
            {interimSegment && (
              <Box opacity={0.6}>
                {interimSegment.speaker !== null && (
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color={getSpeakerColor(interimSegment.speaker)}
                    fontSize="sm"
                  >
                    {getSpeakerLabel(interimSegment.speaker)}:{' '}
                  </Text>
                )}
                <Text
                  as="span"
                  fontSize="md"
                  lineHeight="1.6"
                  fontStyle="italic"
                >
                  {interimSegment.text}
                </Text>
              </Box>
            )}
          </Box>
        ) : (
          <Text color="gray.500" fontStyle="italic">
            {isConnected
              ? 'Waiting for speech...'
              : 'Connecting to live transcription...'}
          </Text>
        )}
      </Box>

      <Text color="gray.500" fontSize="xs" mt={2}>
        This is a live preview. The final transcript will be generated after
        recording ends.
      </Text>
    </Box>
  )
}
